import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

export default function ContactModal({ open, onClose }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const close = () => {
    onClose();
    setTimeout(() => {
      setSent(false);
      setError(null);
    }, 400);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    const data = Object.fromEntries(new FormData(e.target));
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("send failed");
      setSent(true);
    } catch {
      setError("that didn't send. try again?");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm"
          />
          <motion.div
            data-testid="contact-modal"
            initial={{ opacity: 0, y: 60, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 60, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-4 top-[12vh] sm:inset-x-0 sm:mx-auto z-[90] sm:max-w-md bg-paper text-ink border-2 border-black shadow-[10px_10px_0_#00F0FF]"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b-2 border-black">
              <p className="font-display text-xl tracking-wide">TALK TO US.</p>
              <button
                data-testid="contact-close-button"
                onClick={close}
                className="font-sans font-bold text-sm tracking-[0.2em] hover:text-pink transition-colors"
              >
                CLOSE ✕
              </button>
            </div>

            <div className="px-6 py-6">
              {sent ? (
                <div className="text-center py-6" data-testid="contact-success">
                  <p className="font-display text-4xl sm:text-5xl leading-none">
                    SHUTUP <span className="text-turq">RECEIVED.</span>
                  </p>
                  <p className="font-hand text-2xl text-black/60 mt-5 rotate-1">
                    your words are on their way to the studio inbox.
                  </p>
                  <button
                    data-testid="contact-done-button"
                    onClick={close}
                    className="mt-8 w-full bg-black text-paper font-sans font-bold tracking-[0.15em] py-4 hover:bg-pink hover:text-black transition-colors"
                  >
                    BACK TO THE NOISE →
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
                  <label className="block">
                    <span className="font-sans text-[10px] font-bold tracking-[0.25em] text-black/60">NAME</span>
                    <input
                      required
                      type="text"
                      name="name"
                      data-testid="contact-name"
                      className="mt-1.5 w-full border-2 border-black bg-transparent px-4 py-3 font-sans text-sm outline-none focus:bg-acid/30"
                    />
                  </label>
                  <label className="block">
                    <span className="font-sans text-[10px] font-bold tracking-[0.25em] text-black/60">EMAIL</span>
                    <input
                      required
                      type="email"
                      name="email"
                      data-testid="contact-email"
                      className="mt-1.5 w-full border-2 border-black bg-transparent px-4 py-3 font-sans text-sm outline-none focus:bg-acid/30"
                    />
                  </label>
                  <label className="block">
                    <span className="font-sans text-[10px] font-bold tracking-[0.25em] text-black/60">MESSAGE</span>
                    <textarea
                      required
                      rows={4}
                      name="message"
                      data-testid="contact-message"
                      placeholder="say it. all of it."
                      className="mt-1.5 w-full border-2 border-black bg-transparent px-4 py-3 font-hand text-xl outline-none focus:bg-acid/30 placeholder:text-black/30 resize-none"
                    />
                  </label>
                  {error && (
                    <p className="font-sans text-blood text-xs font-bold tracking-[0.15em]" data-testid="contact-error">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="contact-send-button"
                    disabled={sending}
                    className="w-full bg-black text-paper font-sans font-bold tracking-[0.15em] py-4 hover:bg-pink hover:text-black transition-colors disabled:opacity-60"
                  >
                    {sending ? "SENDING…" : "SEND IT →"}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
